/**
 * VAT calculation utilities for backoffice UI
 */

import { EU_VAT_RATES } from '@mygate/common';

/**
 * EU countries with labels and flag emojis
 */
export const EU_COUNTRIES = [
  { code: 'AT', label: 'Austria 🇦🇹' },
  { code: 'BE', label: 'Belgium 🇧🇪' },
  { code: 'BG', label: 'Bulgaria 🇧🇬' },
  { code: 'HR', label: 'Croatia 🇭🇷' },
  { code: 'CY', label: 'Cyprus 🇨🇾' },
  { code: 'CZ', label: 'Czech Republic 🇨🇿' },
  { code: 'DK', label: 'Denmark 🇩🇰' },
  { code: 'EE', label: 'Estonia 🇪🇪' },
  { code: 'FI', label: 'Finland 🇫🇮' },
  { code: 'FR', label: 'France 🇫🇷' },
  { code: 'DE', label: 'Germany 🇩🇪' },
  { code: 'GR', label: 'Greece 🇬🇷' },
  { code: 'HU', label: 'Hungary 🇭🇺' },
  { code: 'IE', label: 'Ireland 🇮🇪' },
  { code: 'IT', label: 'Italy 🇮🇹' },
  { code: 'LV', label: 'Latvia 🇱🇻' },
  { code: 'LT', label: 'Lithuania 🇱🇹' },
  { code: 'LU', label: 'Luxembourg 🇱🇺' },
  { code: 'MT', label: 'Malta 🇲🇹' },
  { code: 'NL', label: 'Netherlands 🇳🇱' },
  { code: 'PL', label: 'Poland 🇵🇱' },
  { code: 'PT', label: 'Portugal 🇵🇹' },
  { code: 'RO', label: 'Romania 🇷🇴' },
  { code: 'SK', label: 'Slovakia 🇸🇰' },
  { code: 'SI', label: 'Slovenia 🇸🇮' },
  { code: 'ES', label: 'Spain 🇪🇸' },
  { code: 'SE', label: 'Sweden 🇸🇪' },
];

/**
 * Popular non-EU countries (0% VAT)
 */
export const NON_EU_COUNTRIES = [
  { code: 'GB', label: 'United Kingdom 🇬🇧' },
  { code: 'CH', label: 'Switzerland 🇨🇭' },
  { code: 'NO', label: 'Norway 🇳🇴' },
  { code: 'US', label: 'United States 🇺🇸' },
  { code: 'CA', label: 'Canada 🇨🇦' },
  { code: 'AE', label: 'United Arab Emirates 🇦🇪' },
  { code: 'SG', label: 'Singapore 🇸🇬' },
  { code: 'AU', label: 'Australia 🇦🇺' },
  { code: 'XX', label: 'Other Non-EU 🌍' },
];

/**
 * All countries (EU + Non-EU)
 */
export const ALL_COUNTRIES = [...EU_COUNTRIES, ...NON_EU_COUNTRIES];

/**
 * VAT breakdown result
 */
export interface VATBreakdown {
  grossAmount: number;
  netAmount: number;
  vatRate: number;
  vatAmount: number;
  customerCountry: string;
}

/**
 * Calculate VAT breakdown from gross amount
 * Formula: netAmount = grossAmount / (1 + vatRate)
 * @param grossAmount - Gross amount (VAT-inclusive price)
 * @param customerCountry - ISO 3166-1 alpha-2 country code
 * @returns VAT breakdown with all amounts
 */
export function calculateVAT(grossAmount: number, customerCountry: string): VATBreakdown {
  // Get VAT rate for country
  const vatRate = EU_VAT_RATES[customerCountry] || 0;

  // Calculate net amount (working backwards from gross)
  const netAmount = vatRate > 0 ? grossAmount / (1 + vatRate) : grossAmount;

  // Calculate VAT amount
  const vatAmount = grossAmount - netAmount;

  return {
    grossAmount: Math.round(grossAmount * 100) / 100,
    netAmount: Math.round(netAmount * 100) / 100,
    vatRate,
    vatAmount: Math.round(vatAmount * 100) / 100,
    customerCountry,
  };
}
